import {
  Calendar,
  Clock,
  GraduationCap,
  Languages,
  MapPin,
  Users,
} from 'lucide-react';
import { Link, useParams } from 'react-router';

import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@workspace/shadcn';

import {
  formatCourseType,
  formatDateRange,
  formatDuration,
  formatLevel,
  formatPrice,
  useCourse,
} from '../../lib/api.ts';
import { usePageMeta } from '../../lib/seo.tsx';
import { CourseModulesTabs } from '../components/course-modules-tabs.tsx';
import { Section } from '../components/section.tsx';
import NotFound from './not-found.tsx';

function DetailRow({
  icon: Icon,
  children,
  compact,
}: {
  icon: typeof Clock;
  children: React.ReactNode;
  compact?: boolean;
}) {
  return (
    <div
      className={compact ? 'flex items-center text-xs' : 'flex items-center text-sm'}
    >
      <Icon
        className={
          compact ? 'text-primary me-1 h-3 w-3' : 'text-primary me-3 h-5 w-5'
        }
      />
      <span>{children}</span>
    </div>
  );
}

const PORTAL_URL = import.meta.env.VITE_USER_APP_URL ?? '/';

export default function CourseDetail() {
  const { slug = '' } = useParams();
  const { data: course, isLoading, isError } = useCourse(slug);

  usePageMeta({
    title: course ? `ITGL Academy | ${course.title}` : 'ITGL Academy',
    description:
      course?.oneLiner ??
      'Advance your healthcare career with our cutting-edge courses.',
    pathname: `/courses/${slug}`,
    ogImage: course?.imageUrl
      ? `https://education.kfshi.com.sa${course.imageUrl}`
      : undefined,
  });

  if (isError) return <NotFound />;

  // Coming-soon courses were excluded from getStaticPaths on the original site,
  // so their detail URLs never resolved.
  if (course?.comingSoon) return <NotFound />;

  if (isLoading || !course) {
    return (
      <Section className="mt-16">
        <Skeleton className="mb-4 h-10 w-2/3" />
        <Skeleton className="mb-8 h-6 w-1/2" />
        <Skeleton className="h-[420px] w-full rounded-lg" />
      </Section>
    );
  }

  const duration = formatDuration(course.durationHours);

  const details = (compact: boolean) => (
    <>
      <DetailRow icon={Calendar} compact={compact}>
        {formatDateRange(course.startDate, course.endDate)}
      </DetailRow>
      {duration && (
        <DetailRow icon={Clock} compact={compact}>
          {duration}
        </DetailRow>
      )}
      <DetailRow icon={GraduationCap} compact={compact}>
        {formatLevel(course.level)}
      </DetailRow>
      {course.languages.length > 0 && (
        <DetailRow icon={Languages} compact={compact}>
          {course.languages.join(', ')}
        </DetailRow>
      )}
      {course.trainingMethod && (
        <DetailRow icon={Users} compact={compact}>
          {formatCourseType(course.trainingMethod)}
        </DetailRow>
      )}
      {course.trainingMethod === 'IN_PERSON' && course.location && (
        <DetailRow icon={MapPin} compact={compact}>
          {course.location}
        </DetailRow>
      )}
    </>
  );

  return (
    <Section className="relative pb-20 md:pb-0 lg:pb-4">
      <div className="relative mt-16 grid grid-cols-1 gap-8 pb-0 md:grid-cols-3">
        <div className="md:col-span-2">
          <h1 className="text-primary mb-2 text-4xl font-bold">
            {course.title}
          </h1>
          <p className="text-muted-foreground mb-6 text-xl">
            {course.oneLiner}
          </p>

          <div className="group/feature relative mb-8 aspect-16/10 w-full overflow-hidden rounded-lg shadow-lg">
            <img
              src={course.imageUrl}
              alt={course.title}
              className="h-full w-full object-cover transition-transform duration-300 group-hover/feature:scale-110"
            />
            {course.category && (
              <div className="absolute start-4 top-4 z-10">
                <Badge className="bg-primary text-primary-foreground">
                  {course.category}
                </Badge>
              </div>
            )}
          </div>

          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-primary text-2xl">
                Course Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground/90">{course.description}</p>
            </CardContent>
          </Card>

          {course.objectives.length > 0 && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-primary text-2xl">
                  Learning Objectives
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-foreground/90 list-disc space-y-2 ps-5">
                  {course.objectives.map((objective) => (
                    <li key={objective}>{objective}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {(course.targetedAudience || course.targetedAreas.length > 0) && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-primary text-xl">
                  Target Audience
                </CardTitle>
              </CardHeader>
              <CardContent>
                {course.targetedAudience && (
                  <p className="text-foreground/90 mb-4">
                    {course.targetedAudience}
                  </p>
                )}
                <ul className="text-foreground/90 list-disc space-y-1 ps-5">
                  {course.targetedAreas.map((area) => (
                    <li key={area}>{area}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {course.prerequisites && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-primary text-xl">
                  Prerequisites
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-foreground/90">{course.prerequisites}</p>
              </CardContent>
            </Card>
          )}

          {course.showModules && (
            <CourseModulesTabs modules={course.modules} />
          )}
        </div>

        <div className="bg-background z-[3000] md:hidden">
          <Card className="fixed inset-x-0 bottom-0 z-50 rounded-none">
            <CardHeader className="p-3">
              <CardTitle className="text-primary flex items-center justify-between text-xl">
                Course Details
                <span className="text-2xl font-bold">
                  {formatPrice(course.price)}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-3 gap-2 p-3">
              <a
                href={`${PORTAL_URL}/signup?course=${course.slug}`}
                className="bg-primary text-primary-foreground col-span-3 rounded-md px-4 py-2 text-center font-medium"
              >
                Enroll Now
              </a>
              {details(true)}
            </CardContent>
          </Card>
        </div>

        <div className="bg-background z-50 hidden md:block">
          <Card className="sticky top-24 overflow-hidden">
            <CardHeader className="bg-primary p-6">
              <CardTitle className="text-primary-foreground text-3xl font-bold">
                {formatPrice(course.price)}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <h2 className="text-primary mb-4 text-xl font-semibold">
                Course Details
              </h2>
              <div className="space-y-4">
                {details(false)}

                {course.instructors.length > 0 && (
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    {course.instructors.map((instructor) => (
                      <Link
                        key={instructor.slug}
                        to={`/instructors/${instructor.slug}`}
                        className="hover:text-primary flex items-center gap-1 font-medium"
                      >
                        {instructor.imageUrl && (
                          <img
                            src={instructor.imageUrl}
                            alt={instructor.name}
                            className="size-6 rounded-full object-cover"
                          />
                        )}
                        <span>{instructor.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <a
                href={`${PORTAL_URL}/signup?course=${course.slug}`}
                className="bg-primary text-primary-foreground mt-6 block w-full rounded-md py-3 text-center text-lg font-medium"
              >
                Enroll Now
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </Section>
  );
}
